import { useState, useMemo } from 'react';
import { apiFetch } from '../api';
import './ModalTheme.css';

/**
 * Modal for assigning a grouped purchase price to multiple inventory items.
 *
 * Props:
 *   user          – logged-in username
 *   items         – inventory items to group (each has assetId, marketHashName, exterior)
 *   prices        – { [marketHashName]: { starting_at: { price: number } } } (Buff163)
 *   onSave        – (groupedPurchase) => void
 *   onClose       – () => void
 *   variant       – 'zone' | 'classic' skin, see ModalTheme.css
 */
export default function GroupedPurchaseModal({ user, items, prices, onSave, onClose, variant = 'classic' }) {
  const [totalPrice, setTotalPrice] = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  // Resolve current market price for each item
  const itemsWithPrice = useMemo(() => {
    return items.map(item => {
      const p = prices?.[item.marketHashName ?? item.market_hash_name]?.starting_at?.price ?? null;
      return { ...item, currentPrice: p };
    });
  }, [items, prices]);

  const missingPrices = itemsWithPrice.filter(i => i.currentPrice == null);

  // Calculate preview
  const preview = useMemo(() => {
    const total = parseFloat(totalPrice);
    if (!total || total <= 0 || missingPrices.length > 0) return null;

    const validItems = itemsWithPrice.filter(i => i.currentPrice != null);
    const combined   = validItems.reduce((s, i) => s + i.currentPrice, 0);
    if (combined <= 0) return null;

    const pct         = total / combined * 100;
    const displayPct  = Math.round(pct * 10) / 10;

    // Calculate per-item prices using largest-remainder
    const totalCents = Math.round(total * 100);
    const withRaw    = validItems.map(i => ({ ...i, rawCents: (i.currentPrice / combined) * totalCents }));
    const withRound  = withRaw.map(i => ({ ...i, roundedCents: Math.round(i.rawCents) }));
    const sumRounded = withRound.reduce((s, i) => s + i.roundedCents, 0);
    let remaining    = totalCents - sumRounded;

    if (remaining !== 0) {
      const sign   = remaining > 0 ? 1 : -1;
      const sorted = withRound.map((i, idx) => ({ idx, f: i.rawCents - Math.floor(i.rawCents) }))
        .sort((a, b) => sign > 0 ? b.f - a.f : a.f - b.f);
      for (let i = 0; i < Math.abs(remaining) && i < sorted.length; i++) {
        withRound[sorted[i].idx].roundedCents += sign;
      }
    }

    return {
      combined:   Math.round(combined * 100) / 100,
      pct,
      displayPct,
      items: withRound.map(i => ({ ...i, assigned: i.roundedCents / 100 })),
    };
  }, [totalPrice, itemsWithPrice, missingPrices]);

  const save = async () => {
    if (!preview) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        items: itemsWithPrice
          .filter(i => i.currentPrice != null)
          .map(i => ({ assetId: i.assetId, marketValueAtCreation: i.currentPrice })),
        totalPurchasePrice: parseFloat(totalPrice),
        currency: 'USD',
      };

      const res  = await apiFetch(`/api/grouped-purchases/${user}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');

      // Enrich server response items with original inventory metadata
      // so the caller can create tracker rows without a separate lookup
      const enrichedItems = data.groupedPurchase.items.map(gpItem => {
        const orig = itemsWithPrice.find(i => i.assetId === gpItem.assetId);
        return {
          ...gpItem,
          marketHashName:      orig?.marketHashName ?? orig?.market_hash_name ?? '',
          exterior:            orig?.exterior            ?? null,
          iconUrl:             orig?.iconUrl             ?? null,
          inspectLink:         orig?.inspectLink         ?? null,
          stickers:            orig?.stickers            ?? [],
          charms:              orig?.charms              ?? [],
          tradeProtected:      orig?.tradeProtected      ?? false,
          tradeProtectedUntil: orig?.tradeProtectedUntil ?? null,
          float:               orig?.float               ?? null,
          paintSeed:           orig?.paintSeed           ?? null,
        };
      });

      onSave?.({ ...data.groupedPurchase, enrichedItems });
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const fmtUSD = v => {
    if (v == null) return '—';
    const rounded = Math.round(v * 100) / 100;
    return '$' + rounded.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  };
  const fmtPct = v => {
    if (v == null) return '—';
    const r = Math.round(v * 10) / 10;
    return (r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)) + '%';
  };

  return (
    <div className={`mdl-overlay ${variant}`} onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className="mdl-panel narrow" role="dialog" aria-modal="true" aria-label="Assign group purchase price">
        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">Assign Group Purchase Price</div>
            <div className="mdl-sub">
              {items.length} item{items.length !== 1 ? 's' : ''} selected. Enter the total amount you paid for all of them.
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <div className="mdl-stack">
            {/* Missing price warning */}
            {missingPrices.length > 0 && (
              <div className="mdl-card caution">
                <div className="mdl-card-title">
                  {missingPrices.length} item{missingPrices.length !== 1 ? 's have' : ' has'} no current market price
                </div>
                <div className="mdl-card-hint">
                  They cannot be included. Sync prices or remove them before saving.
                </div>
                <ul className="mdl-list" style={{ marginTop: 6 }}>
                  {missingPrices.slice(0, 5).map(i => <li key={i.assetId}>{i.marketHashName ?? i.market_hash_name}</li>)}
                  {missingPrices.length > 5 && <li>…and {missingPrices.length - 5} more</li>}
                </ul>
              </div>
            )}

            {/* Item list */}
            <div className="mdl-rows">
              {itemsWithPrice.map(item => (
                <div key={item.assetId} className="mdl-rowline">
                  <span className="name">{item.marketHashName ?? item.market_hash_name}</span>
                  <span className="val">{fmtUSD(item.currentPrice)}</span>
                  {preview && (
                    <span className="out">
                      → {fmtUSD(preview.items.find(pi => pi.assetId === item.assetId)?.assigned)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Price input */}
            <div>
              <span className="mdl-label">Total amount paid (USD)</span>
              <input type="number" className="mdl-input" min="0" step="0.01"
                value={totalPrice} onChange={e => setTotalPrice(e.target.value)} placeholder="0.00" />
            </div>

            {/* Preview */}
            {preview && (
              <div className="mdl-card">
                <div className="mdl-kv">
                  <span className="k">Combined market value</span>
                  <span className="v">{fmtUSD(preview.combined)}</span>
                </div>
                <div className="mdl-kv">
                  <span className="k">You paid</span>
                  <span className="v big">{fmtPct(preview.pct)} of market</span>
                </div>
                <div className="mdl-kv">
                  <span className="k">Per item (avg)</span>
                  <span className="v">{fmtUSD(parseFloat(totalPrice) / items.length)}</span>
                </div>
              </div>
            )}

            {error && <div className="mdl-card-hint mdl-bad">{error}</div>}
          </div>
        </div>

        <div className="mdl-foot">
          <button className="mdl-btn" onClick={onClose}>Cancel</button>
          <button className="mdl-btn primary" onClick={save}
            disabled={!preview || saving || missingPrices.length > 0}>
            {saving ? 'Saving…' : 'Save Group'}
          </button>
        </div>
      </div>
    </div>
  );
}
